import { useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { OrderCancelRequestDto } from '@tiendanube/common';
import {
  Status as StatusEnum,
  FulfillmentPreferenceType as FulfillmentPreferenceTypeEnum,
} from '@tiendanube/common/src/enums';
import { ErrorState, InterfaceLabel } from '@tiendanube/components';
import { BoxUnpackedIcon } from '@tiendanube/icons';
import { useNavegate } from 'App/hooks';
import {
  ActionProp,
  ApplicationsDesktop,
  HeaderContent,
  HeaderTop,
  IonPageStratus,
} from 'commons/components';
import { useAsyncFunc, useModal } from 'commons/hooks';
import { logEvent } from 'commons/utils/tracking';
import { openWindow } from 'commons/utils/window';
import { NUVEM_ENVIO_VIEW } from 'config/trackingEvents';
import { useHasPermission } from 'domains/Auth/hooks';
import { NE_PRINT_LABEL_SOURCE } from 'domains/NuvemEnvioCommons/constants';
import { OrdersPrintLabelModal } from 'domains/NuvemEnvioCommons/Orders/components/PrintLabelModal';
import useNuvemEnvioPrintLabel from 'domains/NuvemEnvioCommons/Orders/hooks/useNuvemEnvioPrintLabel/useNuvemEnvioPrintLabel';
import { isNuvemEnvioLink } from 'domains/NuvemEnvioCommons/Orders/utils';
import HeaderOrderMobile from 'domains/Orders/components/HeaderOrderMobile';
import useOrderWholesaleDetails from 'domains/Orders/Orders/hooks/useOrderWholesaleDetails';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { useAppsLinks, useStoreInfo } from 'domains/PartnersApps/hooks';
import {
  InsufficientStockModal,
  OrderDetails,
  OrderDetailsSkeleton,
  SingleOrderCancelModal,
} from './components';
import ActionsOrderDesktop from './components/ActionsOrderDesktop';
import {
  fulfillment as fulfillmentLabelStyle,
  payment as paymentLabelStyle,
  order as orderLabelStyle,
} from './components/commons/Status/status';
import useActionsToast from './components/OrderDetails/useActionsToast';
import { useTimelineSection } from './components/TimelineSection';
import { useFetchOrdersMetafields } from './hooks';
import useOrderActions from './hooks/useOrderActions';
import { useOrderDetails, useOrdersList } from '../../hooks';
import { useCanBeRefunded } from '../hooks';

import './OrderDetail.scss';

function OrderDetailsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const t = useTranslationOrders();
  const { goTo, goBack, replace } = useNavegate();
  const {
    orderDetails,
    isError,
    isLoading,
    archiveOrder,
    cancelOrder,
    openOrder,
    paidOrder,
    partiallyPaidOrder,
    remarkOrder,
    packOrder,
    fulfillOrder,
    editDeliveryAddress,
    unpackOrder,
  } = useOrderDetails(id);
  const { canBeRefundedThroughCancelForm } = useCanBeRefunded(orderDetails);
  const { id: storeId } = useStoreInfo();
  const { ordersIds } = useOrdersList();
  const { getAppsLinks } = useAppsLinks();
  useTimelineSection({ orderId: id, fetch: true });
  const { onError, onCancelSuccess, onArchiveSuccess, onOpenSuccess } =
    useActionsToast();

  const {
    printNuvemEnvioLabels,
    showNuvemEnvioPrintLabelModal,
    closeNuvemEnvioPrintLabelModal,
  } = useNuvemEnvioPrintLabel();

  const { isWholesale } = useOrderWholesaleDetails(id);
  const firstFulfillmentOrderId =
    orderDetails?.fulfillmentOrders?.[0]?.id ?? undefined;

  useFetchOrdersMetafields(id);
  const [showCancelModal, openModal, handleHideModal] = useModal();
  const { actions, updateMetadata, handleHideReopenModal, showReopenModal } =
    useOrderActions(
      orderDetails,
      openModal,
      onArchiveSuccess,
      onOpenSuccess,
      openOrder,
      archiveOrder,
      onError,
    );

  const [handleCancelOrder, isCanceling] = useAsyncFunc<
    OrderCancelRequestDto,
    void
  >(
    async (data) => {
      if (data) {
        await cancelOrder(data);
        goTo('/orders');
      }
    },
    onCancelSuccess,
    onError,
  );

  const title = t('detail.title', { number: orderDetails?.number });

  const filteredActions = useMemo<ActionProp[]>(() => {
    if (!actions) return [];

    return actions.filter((a) => !a.disabled);
  }, [actions]);

  const headerLabels = useMemo<InterfaceLabel[]>(() => {
    if (!orderDetails) return [];

    const wholesaleLabel: InterfaceLabel = {
      id: 'wholesaleLabel',
      appearance: 'primary',
      icon: BoxUnpackedIcon,
      label: t('status.wholesale'),
    };

    if (orderDetails.status !== StatusEnum.OPEN) {
      const orderLabel = orderLabelStyle(orderDetails.status);
      const label =
        orderDetails.status === StatusEnum.CANCELLED &&
        orderDetails.cancelReason === 'automatic'
          ? t('status.automaticCancelled')
          : t(`status.${orderLabel.status}`);
      return [
        {
          ...orderLabel,
          id: orderLabel.status,
          label,
        },
        ...(isWholesale ? [wholesaleLabel] : []),
      ];
    }

    const paymentLabel = paymentLabelStyle(orderDetails.payment.status);

    const fulfillmentLabel = fulfillmentLabelStyle(
      orderDetails.fulfillment.status as StatusEnum,
      orderDetails.fulfillment.preference.type as FulfillmentPreferenceTypeEnum,
      orderDetails.isShippablePickup,
    );

    return [
      {
        ...paymentLabel,
        id: paymentLabel.status,
        label: `${t(`status.${paymentLabel.status}`)}`,
      },
      ...(orderDetails.fulfillment.status
        ? [
            {
              ...fulfillmentLabel,
              id: fulfillmentLabel.status,
              label: `${t(
                `status.${
                  fulfillmentLabel.status === 'pickuped' &&
                  orderDetails.isShippablePickup
                    ? 'shipped'
                    : fulfillmentLabel.status
                }`,
              )}`,
            },
          ]
        : []),
      ...(isWholesale ? [wholesaleLabel] : []),
    ];
  }, [orderDetails, t, isWholesale]);

  const hasOrdersList = ordersIds.length > 0;
  const currentOrderIndex = ordersIds.findIndex((orderId) => orderId === id);
  const hasPreviousOrders = hasOrdersList && currentOrderIndex > 0;
  const hasNextOrders =
    hasOrdersList && currentOrderIndex < ordersIds.length - 1;

  const goToPreviousOrder = () => {
    if (hasPreviousOrders) {
      replace(`/orders/${ordersIds[currentOrderIndex - 1]}`);
    }
  };

  const goToNextOrder = () => {
    if (hasNextOrders) {
      replace(`/orders/${ordersIds[currentOrderIndex + 1]}`);
    }
  };

  const printSectionRef = useRef<HTMLDivElement>(null);

  const hasPrintShippingLabelPermission = useHasPermission(
    'print_order_shipping_labels',
  );

  const applicationsActions: ActionProp[] = getAppsLinks(
    'orders_single',
    'default',
  )
    .filter((app) => {
      if (!isNuvemEnvioLink(app.url)) return true;
      return hasPrintShippingLabelPermission;
    })
    .map((app) => ({
      children: app.text,
      onClick: () => {
        if (isNuvemEnvioLink(app.url)) {
          logEvent(NUVEM_ENVIO_VIEW, {
            storeId,
            page: 'order',
            action: 'nuvem envio',
          });
          printNuvemEnvioLabels(
            NE_PRINT_LABEL_SOURCE.ORDER_DETAILS,
            orderDetails?.fulfillmentOrders?.map(
              (fulfillment) => fulfillment.id,
            ) ?? [],
            false,
            orderDetails?.payment.status,
          );
        } else {
          openWindow(`${app.url}&id=${id}`, true);
        }
      },
    }));

  const getHeaderTop = () => {
    const backNavitation = {
      onClick: goBack,
    };

    if (isError || isLoading) return <HeaderTop navigation={backNavitation} />;
    return (
      <HeaderOrderMobile
        navigation={backNavitation}
        actions={filteredActions}
        applications={applicationsActions}
      />
    );
  };

  const getHeaderContent = () => {
    if (isLoading)
      return (
        <HeaderContent title="skeleton" labels={['skeleton', 'skeleton']} />
      );
    if (!orderDetails) return null;

    return (
      <HeaderContent
        title={title}
        actions={<ActionsOrderDesktop actions={actions} />}
        applications={
          <ApplicationsDesktop applications={applicationsActions} />
        }
        paginationPrevious={hasPreviousOrders ? goToPreviousOrder : undefined}
        paginationNext={hasNextOrders ? goToNextOrder : undefined}
      />
    );
  };

  return (
    <IonPageStratus
      width="medium"
      headerTop={getHeaderTop()}
      headerContent={getHeaderContent()}
      headerFixed
    >
      {isError && <ErrorState title={t('errorPage.message')} />}
      {isLoading && <OrderDetailsSkeleton />}
      {!isError && !isLoading && orderDetails && (
        <>
          <div
            ref={printSectionRef}
            className="print-section stratus--order-details"
          >
            <OrderDetails
              labels={headerLabels}
              orderDetails={orderDetails}
              onRemarkOrder={remarkOrder}
              onPaidOrder={paidOrder}
              onPartiallyPaidOrder={partiallyPaidOrder}
              onPackOrder={packOrder}
              onFulfillOrder={fulfillOrder}
              onEditDeliveryAddress={editDeliveryAddress}
              onUnpackOrder={unpackOrder}
            />
          </div>

          {showCancelModal && (
            <SingleOrderCancelModal
              orderId={orderDetails?.number}
              canBeRefunded={canBeRefundedThroughCancelForm()}
              amount={orderDetails?.amounts.total}
              gateway={orderDetails?.payment.gateway || ''}
              show
              hideModal={handleHideModal}
              isPaid={
                orderDetails.payment.status === StatusEnum.PAID &&
                orderDetails.status === StatusEnum.OPEN
              }
              appliedAction={handleCancelOrder}
              isLoading={isCanceling}
              fulfillmentOrderId={firstFulfillmentOrderId}
              orderIdentification={orderDetails?.id}
              onError={onError}
            />
          )}
          {showReopenModal && (
            <InsufficientStockModal
              open={showReopenModal}
              onConfirm={() => openOrder(false)}
              onDismiss={handleHideReopenModal}
              updateMetadata={updateMetadata}
              toastSuccess={onOpenSuccess}
              onError={onError}
            />
          )}

          {showNuvemEnvioPrintLabelModal && (
            <OrdersPrintLabelModal
              onClose={closeNuvemEnvioPrintLabelModal}
              orderDetails={orderDetails}
              source={NE_PRINT_LABEL_SOURCE.ORDER_DETAILS}
            />
          )}
        </>
      )}
    </IonPageStratus>
  );
}

export default OrderDetailsPage;
